import { h } from "@stencil/core";
const DEFAULT_DRAG_DISABLED_VALUE = false;
const DEFAULT_DROP_DISABLED_VALUE = false;
const DEFAULT_EDITABLE_ITEMS_VALUE = true;
const defaultRenderItem = (itemModel, treeState, treeHasFilter, lastItem, level) => (treeState.filterType === "none" || itemModel.render !== false) && (h("ch-tree-view-item", { id: itemModel.id, caption: itemModel.caption, checkbox: itemModel.checkbox ?? treeState.checkbox, checked: itemModel.checked ?? treeState.checked, class: itemModel.class, disabled: itemModel.disabled, downloading: itemModel.downloading, dragDisabled: itemModel.dragDisabled ?? treeState.dragDisabled, dropDisabled: itemModel.dropDisabled ?? treeState.dropDisabled, editable: itemModel.editable ?? treeState.editableItems, expandableButton: itemModel.expandableButton, expanded: itemModel.expanded, indeterminate: itemModel.indeterminate, lastItem: lastItem, lazyLoad: itemModel.lazy, leaf: itemModel.leaf, leftImgSrc: itemModel.leftImgSrc, level: level, metadata: itemModel.metadata, rightImgSrc: itemModel.rightImgSrc, selected: itemModel.selected, showLines: treeState.showLines, toggleCheckboxes: itemModel.toggleCheckboxes ?? treeState.toggleCheckboxes }, !itemModel.leaf &&
    itemModel.items != null &&
    itemModel.items.map((subModel, index) => defaultRenderItem(subModel, treeState, treeHasFilter, treeState.showLines &&
        (treeHasFilter && itemModel.lastItemId !== ""
            ? subModel.id === itemModel.lastItemId
            : index === itemModel.items.length - 1), level + 1))));
export class ChTreeViewRenderWrapper {
    constructor() {
        this.checkbox = false;
        this.checked = false;
        this.checkDroppableZoneCallback = undefined;
        this.cssClass = undefined;
        this.dragDisabled = DEFAULT_DRAG_DISABLED_VALUE;
        this.dropDisabled = DEFAULT_DROP_DISABLED_VALUE;
        this.dropItemsCallback = undefined;
        this.editableItems = DEFAULT_EDITABLE_ITEMS_VALUE;
        this.expandableButton = "decorative";
        this.expandOnClick = true;
        this.filter = undefined;
        this.filterList = [];
        this.filterOptions = {};
        this.filterType = "none";
        this.lazyLoadTreeItemsCallback = undefined;
        this.modifyItemCaptionCallback = undefined;
        this.multiSelection = false;
        this.renderItem = defaultRenderItem;
        this.showLines = "none";
        this.sortItemsCallback = undefined;
        this.toggleCheckboxes = false;
        this.treeModel = [];
    }
    // Refs
    treeRef;
    /**
     * Fired when the checked items change.
     * This event does not take into account the currently filtered items.
     */
    checkedItemsChange;
    /**
     * Fired when an element displays its contextmenu.
     */
    itemContextmenu;
    /**
     * Fired when the user interacts with an item in a way that its reference
     * must be opened.
     */
    itemOpenReference;
    /**
     * Fired when the selected items change.
     */
    selectedItemsChange;
    /**
     * Given an item id, an array of items to add, the download status and the
     * lazy state, updates the item's UI Model.
     */
    async loadLazyContent(itemId, items, downloading = false, lazy = false) {
        this.treeRef.loadLazyContent(itemId, items, downloading, lazy);
    }
    /**
     * Given an item id, it displays and scrolls into the item view.
     */
    async scrollIntoVisible(treeItemId) {
        this.treeRef.scrollIntoVisible(treeItemId);
    }
    /**
     * This method is used to toggle a tree item by the tree item id/ids.
     *
     * @param treeItemIds An array id the tree items to be toggled.
     * @param expand A boolean indicating that the tree item should be expanded or collapsed. (optional)
     * @returns The modified items after the method was called.
     */
    async toggleItems(treeItemIds, expand) {
        return this.treeRef.toggleItems(treeItemIds, expand);
    }
    /**
     * Given a subset of item's properties, it updates all item UI models.
     */
    async updateAllItemsProperties(properties) {
        this.treeRef.updateAllItemsProperties(properties);
    }
    /**
     * Given a item list and the properties to update, it updates the properties
     * of the items in the list.
     */
    async updateItemsProperties(items, properties) {
        this.treeRef.updateItemsProperties(items, properties);
    }
    /**
     * Update the information about the valid droppable zones.
     * @param requestTimestamp Time where the request to the server was made. Useful to avoid having old information.
     * @param newContainerId ID of the container where the drag is trying to be made.
     * @param draggedItems Information about the dragged items.
     * @param validDrop Current state of the droppable zone.
     */
    async updateValidDropZone(requestTimestamp, newContainerId, draggedItems, validDrop) {
        this.treeRef.updateValidDropZone(requestTimestamp, newContainerId, draggedItems, validDrop);
    }
    updateCheckboxValue(event) {
        event.stopPropagation();
        this.checkedItemsChange.emit(event.detail);
    }
    handleOpenReference(event) {
        event.stopPropagation();
        this.itemOpenReference.emit(event.detail);
    }
    render() {
        return (h("ch-tree-view-render", { checkbox: this.checkbox, checked: this.checked, checkDroppableZoneCallback: this.checkDroppableZoneCallback, cssClass: this.cssClass, dragDisabled: this.dragDisabled, dropDisabled: this.dropDisabled, dropItemsCallback: this.dropItemsCallback, editableItems: this.editableItems, expandableButton: this.expandableButton, expandOnClick: this.expandOnClick, filter: this.filter, filterList: this.filterList, filterOptions: this.filterOptions, filterType: this.filterType, lazyLoadTreeItemsCallback: this.lazyLoadTreeItemsCallback, modifyItemCaptionCallback: this.modifyItemCaptionCallback, multiSelection: this.multiSelection, renderItem: this.renderItem, showLines: this.showLines, sortItemsCallback: this.sortItemsCallback, toggleCheckboxes: this.toggleCheckboxes, treeModel: this.treeModel, ref: el => (this.treeRef = el) }));
    }
    static get is() { return "ch-tree-view-render-wrapper"; }
    static get originalStyleUrls() {
        return {
            "$": ["tree-view-render-wrapper.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["tree-view-render-wrapper.css"]
        };
    }
    static get properties() {
        return {
            "checkbox": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set this attribute if you want display a checkbox in all items by default."
                },
                "attribute": "checkbox",
                "reflect": false,
                "defaultValue": "false"
            },
            "checked": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set this attribute if you want the checkbox to be checked in all items by\ndefault.\nOnly works if `checkbox = true`"
                },
                "attribute": "checked",
                "reflect": false,
                "defaultValue": "false"
            },
            "checkDroppableZoneCallback": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "(\n    dropInformation: TreeViewDropCheckInfo\n  ) => Promise<boolean>",
                    "resolved": "(dropInformation: TreeViewDropCheckInfo) => Promise<boolean>",
                    "references": {
                        "TreeViewDropCheckInfo": {
                            "location": "import",
                            "path": "../../tree-view/tree-view/types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewDropCheckInfo"
                        },
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Callback that is executed when an element tries to drop in another item of\nthe tree. Returns whether the drop is valid."
                }
            },
            "cssClass": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A CSS class to set as the `ch-tree-view` element class."
                },
                "attribute": "css-class",
                "reflect": false
            },
            "dragDisabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the drag operation is disabled in all\nitems by default. If `true`, the items can't be dragged."
                },
                "attribute": "drag-disabled",
                "reflect": false,
                "defaultValue": "DEFAULT_DRAG_DISABLED_VALUE"
            },
            "dropDisabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the drop operation is disabled in all\nitems by default. If `true`, the items won't accept any drops."
                },
                "attribute": "drop-disabled",
                "reflect": false,
                "defaultValue": "DEFAULT_DROP_DISABLED_VALUE"
            },
            "dropItemsCallback": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "(\n    dataTransferInfo: TreeViewDataTransferInfo\n  ) => Promise<{ acceptDrop: boolean; items?: TreeViewItemModel[] }>",
                    "resolved": "(dataTransferInfo: TreeViewDataTransferInfo) => Promise<{ acceptDrop: boolean; items?: TreeViewItemModel[]; }>",
                    "references": {
                        "TreeViewDataTransferInfo": {
                            "location": "import",
                            "path": "../../tree-view/tree-view/types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewDataTransferInfo"
                        },
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "TreeViewItemModel": {
                            "location": "import",
                            "path": "../../tree-view/tree-view/types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewItemModel"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Callback that is executed when a list of items request to be dropped into\nanother item."
                }
            },
            "editableItems": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the edit operation is enabled in all\nitems by default. If `true`, the items can edit its caption in place."
                },
                "attribute": "editable-items",
                "reflect": false,
                "defaultValue": "DEFAULT_EDITABLE_ITEMS_VALUE"
            },
            "expandableButton": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"action\" | \"decorative\" | \"no\"",
                    "resolved": "\"action\" | \"decorative\" | \"no\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies what kind of expandable button is displayed in the items by\ndefault.\n - `\"expandableButton\"`: Expandable button that allows to expand/collapse\n    the items of the control.\n - `\"decorative\"`: Only a decorative icon is rendered to display the state\n    of the item."
                },
                "attribute": "expandable-button",
                "reflect": false,
                "defaultValue": "\"decorative\""
            },
            "expandOnClick": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies if a tree-view-item is expanded on click interaction. If `true`\nthe tree-view-item is expanded on click interaction. If `false`, with\nmouse interaction the tree-view-item will only be expanded on double click."
                },
                "attribute": "expand-on-click",
                "reflect": false,
                "defaultValue": "true"
            },
            "filter": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property lets you determine the expression that will be applied to the\nfilter.\nOnly works if `filterType = \"caption\" | \"metadata\"`."
                },
                "attribute": "filter",
                "reflect": false
            },
            "filterList": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "string[]",
                    "resolved": "string[]",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property lets you determine the list of items that will be filtered.\nOnly works if `filterType = \"id-list\"`."
                },
                "defaultValue": "[]"
            },
            "filterOptions": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "TreeViewFilterOptions",
                    "resolved": "{ autoExpand?: boolean; hideMatchesAndShowNonMatches?: boolean; highlightMatchedItems?: boolean; matchCase?: boolean; regularExpression?: boolean; }",
                    "references": {
                        "TreeViewFilterOptions": {
                            "location": "import",
                            "path": "../../renders/tree-view/types",
                            "id": "src/components/renders/tree-view/types.ts::TreeViewFilterOptions"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property lets you determine the options that will be applied to the\nfilter.\nOnly works if `filterType = \"caption\" | \"metadata\"`."
                },
                "defaultValue": "{}"
            },
            "filterType": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "TreeViewFilterType",
                    "resolved": "\"caption\" | \"checked\" | \"list\" | \"metadata\" | \"none\" | \"unchecked\"",
                    "references": {
                        "TreeViewFilterType": {
                            "location": "import",
                            "path": "../../renders/tree-view/types",
                            "id": "src/components/renders/tree-view/types.ts::TreeViewFilterType"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you define what kind of filter is applied to items.\nOnly items that satisfy the filter predicate will be displayed.\n\n| Value       | Details                                                                                        |\n| ----------- | ---------------------------------------------------------------------------------------------- |\n| `checked`   | Show only the items that have a checkbox and are checked.                                      |\n| `unchecked` | Show only the items that have a checkbox and are not checked.                                  |\n| `caption`   | Show only the items whose `caption` satisfies the regex determinate by the `filter` property.  |\n| `metadata`  | Show only the items whose `metadata` satisfies the regex determinate by the `filter` property. |\n| `id-list`   | Show only the items that are contained in the array determinate by the `filterList` property.  |\n| `none`      | Show all items.                                                                                |"
                },
                "attribute": "filter-type",
                "reflect": false,
                "defaultValue": "\"none\""
            },
            "lazyLoadTreeItemsCallback": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "(\n    treeItemId: string\n  ) => Promise<TreeViewItemModel[]>",
                    "resolved": "(treeItemId: string) => Promise<TreeViewItemModel[]>",
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "TreeViewItemModel": {
                            "location": "import",
                            "path": "../../tree-view/tree-view/types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewItemModel"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Callback that is executed when a item request to load its subitems."
                }
            },
            "modifyItemCaptionCallback": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "(\n    treeItemId: string,\n    newCaption: string\n  ) => Promise<TreeViewOperationStatusModifyCaption>",
                    "resolved": "(treeItemId: string, newCaption: string) => Promise<TreeViewOperationStatusModifyCaption>",
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "TreeViewOperationStatusModifyCaption": {
                            "location": "import",
                            "path": "../../renders/tree-view/types",
                            "id": "src/components/renders/tree-view/types.ts::TreeViewOperationStatusModifyCaption"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Callback that is executed when a item request to modify its caption."
                }
            },
            "multiSelection": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set this attribute if you want to allow multi selection of the items."
                },
                "attribute": "multi-selection",
                "reflect": false,
                "defaultValue": "false"
            },
            "renderItem": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "(\n    itemModel: TreeViewItemModel,\n    treeState: ChTreeViewRender,\n    treeHasFilter: boolean,\n    lastItem: boolean,\n    level: number\n  ) => any",
                    "resolved": "(itemModel: TreeViewItemModel, treeState: ChTreeViewRender, treeHasFilter: boolean, lastItem: boolean, level: number) => any",
                    "references": {
                        "TreeViewItemModel": {
                            "location": "import",
                            "path": "../../tree-view/tree-view/types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewItemModel"
                        },
                        "ChTreeViewRender": {
                            "location": "import",
                            "path": "../../renders/tree-view/tree-view-render",
                            "id": "src/components/renders/tree-view/tree-view-render.tsx::ChTreeViewRender"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property allows us to implement custom rendering of tree items."
                },
                "defaultValue": "defaultRenderItem"
            },
            "showLines": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "TreeViewLines",
                    "resolved": "\"all\" | \"last\" | \"none\"",
                    "references": {
                        "TreeViewLines": {
                            "location": "import",
                            "path": "../../tree-view/tree-view/types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewLines"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to display the relation between tree items and tree lists using\nlines."
                },
                "attribute": "show-lines",
                "reflect": false,
                "defaultValue": "\"none\""
            },
            "sortItemsCallback": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "(subModel: TreeViewItemModel[]) => void",
                    "resolved": "(subModel: TreeViewItemModel[]) => void",
                    "references": {
                        "TreeViewItemModel": {
                            "location": "import",
                            "path": "../../tree-view/tree-view/types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewItemModel"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Callback that is executed when the treeModel is changed to order its items."
                }
            },
            "toggleCheckboxes": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set this attribute if you want all the children item's checkboxes to be\nchecked when the parent item checkbox is checked, or to be unchecked when\nthe parent item checkbox is unchecked.\nThis attribute will be used in all items by default."
                },
                "attribute": "toggle-checkboxes",
                "reflect": false,
                "defaultValue": "false"
            },
            "treeModel": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "TreeViewItemModel[]",
                    "resolved": "TreeViewItemModel[]",
                    "references": {
                        "TreeViewItemModel": {
                            "location": "import",
                            "path": "../../tree-view/tree-view/types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewItemModel"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property lets you define the model of the ch-tree-x control."
                },
                "defaultValue": "[]"
            }
        };
    }
    static get events() {
        return [{
                "method": "checkedItemsChange",
                "name": "checkedItemsChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the checked items change.\nThis event does not take into account the currently filtered items."
                },
                "complexType": {
                    "original": "Map<string, TreeViewItemModelExtended>",
                    "resolved": "Map<string, TreeViewItemModelExtended>",
                    "references": {
                        "Map": {
                            "location": "global",
                            "id": "global::Map"
                        },
                        "TreeViewItemModelExtended": {
                            "location": "import",
                            "path": "../../renders/tree-view/types",
                            "id": "src/components/renders/tree-view/types.ts::TreeViewItemModelExtended"
                        }
                    }
                }
            }, {
                "method": "itemContextmenu",
                "name": "itemContextmenu",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when an element displays its contextmenu."
                },
                "complexType": {
                    "original": "TreeViewItemContextMenu",
                    "resolved": "{ id: string; itemRef: HTMLChTreeViewItemElement; metadata: string; contextmenuEvent: PointerEvent; }",
                    "references": {
                        "TreeViewItemContextMenu": {
                            "location": "import",
                            "path": "../../tree-view/tree-view/types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewItemContextMenu"
                        }
                    }
                }
            }, {
                "method": "itemOpenReference",
                "name": "itemOpenReference",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the user interacts with an item in a way that its reference\nmust be opened."
                },
                "complexType": {
                    "original": "TreeViewItemOpenReferenceInfo",
                    "resolved": "{ id: string; leaf: boolean; metadata: string; }",
                    "references": {
                        "TreeViewItemOpenReferenceInfo": {
                            "location": "import",
                            "path": "../../tree-view/tree-view/types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewItemOpenReferenceInfo"
                        }
                    }
                }
            }, {
                "method": "selectedItemsChange",
                "name": "selectedItemsChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the selected items change."
                },
                "complexType": {
                    "original": "Map<string, TreeViewItemSelected>",
                    "resolved": "Map<string, TreeViewItemSelected>",
                    "references": {
                        "Map": {
                            "location": "global",
                            "id": "global::Map"
                        },
                        "TreeViewItemSelected": {
                            "location": "import",
                            "path": "../../tree-view/tree-view/types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewItemSelected"
                        }
                    }
                }
            }];
    }
    static get methods() {
        return {
            "loadLazyContent": {
                "complexType": {
                    "signature": "(itemId: string, items?: TreeViewItemModel[], downloading?: boolean, lazy?: boolean) => Promise<void>",
                    "parameters": [{
                            "name": "itemId",
                            "type": "string",
                            "docs": ""
                        }, {
                            "name": "items",
                            "type": "TreeViewItemModel[]",
                            "docs": ""
                        }, {
                            "name": "downloading",
                            "type": "boolean",
                            "docs": ""
                        }, {
                            "name": "lazy",
                            "type": "boolean",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "TreeViewItemModel": {
                            "location": "import",
                            "path": "../../tree-view/tree-view/types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewItemModel"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Given an item id, an array of items to add, the download status and the\nlazy state, updates the item's UI Model.",
                    "tags": []
                }
            },
            "scrollIntoVisible": {
                "complexType": {
                    "signature": "(treeItemId: string) => Promise<void>",
                    "parameters": [{
                            "name": "treeItemId",
                            "type": "string",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Given an item id, it displays and scrolls into the item view.",
                    "tags": []
                }
            },
            "toggleItems": {
                "complexType": {
                    "signature": "(treeItemIds: string[], expand?: boolean) => Promise<TreeViewItemExpandedInfo[]>",
                    "parameters": [{
                            "name": "treeItemIds",
                            "type": "string[]",
                            "docs": "An array id the tree items to be toggled."
                        }, {
                            "name": "expand",
                            "type": "boolean",
                            "docs": "A boolean indicating that the tree item should be expanded or collapsed. (optional)"
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "TreeViewItemExpandedInfo": {
                            "location": "import",
                            "path": "../../tree-view/tree-view/types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewItemExpandedInfo"
                        }
                    },
                    "return": "Promise<TreeViewItemExpandedInfo[]>"
                },
                "docs": {
                    "text": "This method is used to toggle a tree item by the tree item id/ids.",
                    "tags": [{
                            "name": "param",
                            "text": "treeItemIds An array id the tree items to be toggled."
                        }, {
                            "name": "param",
                            "text": "expand A boolean indicating that the tree item should be expanded or collapsed. (optional)"
                        }, {
                            "name": "returns",
                            "text": "The modified items after the method was called."
                        }]
                }
            },
            "updateAllItemsProperties": {
                "complexType": {
                    "signature": "(properties: { expanded?: boolean; checked?: boolean; }) => Promise<void>",
                    "parameters": [{
                            "name": "properties",
                            "type": "{ expanded?: boolean; checked?: boolean; }",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Given a subset of item's properties, it updates all item UI models.",
                    "tags": []
                }
            },
            "updateItemsProperties": {
                "complexType": {
                    "signature": "(items: string[], properties: TreeViewItemModel) => Promise<void>",
                    "parameters": [{
                            "name": "items",
                            "type": "string[]",
                            "docs": ""
                        }, {
                            "name": "properties",
                            "type": "{ id: string; caption: string; checkbox?: boolean; checked?: boolean; class?: string; disabled?: boolean; downloading?: boolean; dragDisabled?: boolean; dropDisabled?: boolean; editable?: boolean; expanded?: boolean; expandableButton?: \"action\" | \"no\" | \"decorative\"; lastItemId?: string; lazy?: boolean; leaf?: boolean; leftImgSrc?: string; indeterminate?: boolean; items?: TreeViewItemModel[]; metadata?: string; order?: number; render?: boolean; rightImgSrc?: string; selected?: boolean; toggleCheckboxes?: boolean; }",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "TreeViewItemModel": {
                            "location": "import",
                            "path": "../../tree-view/tree-view/types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewItemModel"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Given a item list and the properties to update, it updates the properties\nof the items in the list.",
                    "tags": []
                }
            },
            "updateValidDropZone": {
                "complexType": {
                    "signature": "(requestTimestamp: number, newContainerId: string, draggedItems: GxDataTransferInfo[], validDrop: boolean) => Promise<void>",
                    "parameters": [{
                            "name": "requestTimestamp",
                            "type": "number",
                            "docs": "Time where the request to the server was made. Useful to avoid having old information."
                        }, {
                            "name": "newContainerId",
                            "type": "string",
                            "docs": "ID of the container where the drag is trying to be made."
                        }, {
                            "name": "draggedItems",
                            "type": "GxDataTransferInfo[]",
                            "docs": "Information about the dragged items."
                        }, {
                            "name": "validDrop",
                            "type": "boolean",
                            "docs": "Current state of the droppable zone."
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "GxDataTransferInfo": {
                            "location": "import",
                            "path": "../../../common/types",
                            "id": "src/common/types.ts::GxDataTransferInfo"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Update the information about the valid droppable zones.",
                    "tags": [{
                            "name": "param",
                            "text": "requestTimestamp Time where the request to the server was made. Useful to avoid having old information."
                        }, {
                            "name": "param",
                            "text": "newContainerId ID of the container where the drag is trying to be made."
                        }, {
                            "name": "param",
                            "text": "draggedItems Information about the dragged items."
                        }, {
                            "name": "param",
                            "text": "validDrop Current state of the droppable zone."
                        }]
                }
            }
        };
    }
    static get listeners() {
        return [{
                "name": "checkedItemsChange",
                "method": "updateCheckboxValue",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "itemOpenReference",
                "method": "handleOpenReference",
                "target": undefined,
                "capture": false,
                "passive": false
            }];
    }
}
//# sourceMappingURL=tree-view-render-wrapper.js.map
